/* ==========================================================================
   H3O buy box — tier selector + purchase-mode pricing engine.

   Tier model (matches the store's automatic BxGy discounts exactly):
     tier 1: qty 1, pay for 1
     tier 2: qty 3, pay for 2  ("Buy 2, Get 1 Free")
     tier 3: qty 5, pay for 3  ("Buy 3, Get 2 Free")

   Unit price comes from data-unit-cents (one-time) or the selected selling
   plan option's data-plan-cents (subscribe). Totals shown here are exactly
   what checkout produces: paid_units x unit_cents.
   ========================================================================== */

if (!customElements.get('h3o-buy-box')) {
  customElements.define(
    'h3o-buy-box',
    class H3OBuyBox extends HTMLElement {
      connectedCallback() {
        this.unitCents = parseInt(this.dataset.unitCents, 10) || 0;
        this.currency = this.dataset.currency || 'USD';
        this.hasPlans = this.dataset.hasPlans === 'true';
        this.servings = parseInt(this.dataset.servings, 10) || 30;
        this.mode = 'onetime';

        this.tiers = Array.from(this.querySelectorAll('.h3o-tier'));
        this.modes = Array.from(this.querySelectorAll('.h3o-mode'));
        this.subToggle = this.querySelector('[data-subscribe-toggle]');
        this.frequencyWrap = this.querySelector('[data-frequency-wrap]') || this.querySelector('.h3o-mode__frequency');
        this.frequencySelect = this.querySelector('[data-frequency]');
        this.quantityInput = this.querySelector('[data-quantity-input]');
        this.sellingPlanInput = this.querySelector('[data-selling-plan-input]');
        this.atcLabel = this.querySelector('[data-atc-label]');

        this.section = this.closest('section');
        this.stickyBar = this.section ? this.section.querySelector('[data-sticky-atc]') : null;

        this.fmt = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 2,
        });

        this.tiers.forEach((tier) => {
          tier.querySelector('.h3o-tier__input').addEventListener('change', () => {
            this.selectTier(tier);
          });
        });

        this.modes.forEach((mode) => {
          const inp = mode.querySelector('input');
          if (inp) inp.addEventListener('change', () => this.selectMode(mode.dataset.mode));
        });

        /* initial mode comes from whichever control the markup checked
           (subscribe card ships checked → subscribe is the default mode) */
        const checkedMode = this.modes.find((m) => {
          const inp = m.querySelector('input');
          return inp && inp.checked;
        });
        if (checkedMode) this.mode = checkedMode.dataset.mode;

        /* legacy single-checkbox variant */
        if (this.subToggle) {
          this.mode = this.subToggle.checked ? 'subscribe' : 'onetime';
          this.subToggle.addEventListener('change', () => {
            this.selectMode(this.subToggle.checked ? 'subscribe' : 'onetime');
          });
        }

        if (this.frequencySelect) {
          /* tidy verbose selling-plan names into a concise "Every N month(s)" */
          Array.from(this.frequencySelect.options).forEach((opt) => {
            const t = opt.textContent;
            const m = t.match(/(\d+)\s*month/i);
            const w = t.match(/(\d+)\s*week/i);
            if (m) opt.textContent = 'Every ' + m[1] + ' month' + (m[1] === '1' ? '' : 's');
            else if (/month/i.test(t)) opt.textContent = 'Every month';
            else if (w) opt.textContent = 'Every ' + w[1] + ' week' + (w[1] === '1' ? '' : 's');
            else if (/week/i.test(t)) opt.textContent = 'Every week';
          });
          this.frequencySelect.addEventListener('change', () => { this.render(); this.syncFrequencyUI(); });
          /* replace the native select with a branded custom dropdown */
          this.buildFrequencyUI();
        }

        this.initSticky();
        this.initShipsBy();
        if (this.frequencyWrap) this.frequencyWrap.hidden = this.mode !== 'subscribe';
        // Liquid has already selected the matching allocation and initial price.
        this.render();
        this.querySelectorAll('[data-offer-control]').forEach((control) => { control.disabled = false; });
        this.querySelector('[data-offer-fallback]')?.remove();
      }

      /* "ships by" = today + N business days (honest, computed client-side) */
      initShipsBy() {
        const wrap = this.section ? this.section.querySelector('[data-ships-by]') : null;
        if (!wrap) return;
        const out = wrap.querySelector('[data-ships-date]');
        if (!out) return;
        let days = parseInt(wrap.dataset.leadDays, 10) || 2;
        const d = new Date();
        while (days > 0) {
          d.setDate(d.getDate() + 1);
          if (d.getDay() !== 0 && d.getDay() !== 6) days--;
        }
        out.textContent = d.toLocaleDateString(document.documentElement.lang || 'en', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        });
      }

      /* consumption-matched frequency: qty 1 -> ~1 month, 3 -> ~3 months, 5 -> ~5 months */
      matchFrequencyToTier(qty) {
        if (!this.frequencySelect) return;
        const options = Array.from(this.frequencySelect.options);
        const monthsOf = (text) => {
          const m = text.match(/(\d+)\s*month/i);
          if (m) return parseInt(m[1], 10);
          if (/month/i.test(text)) return 1;
          const w = text.match(/(\d+)\s*week/i);
          if (w) return parseInt(w[1], 10) / 4;
          return null;
        };
        let best = null;
        let bestDiff = Infinity;
        options.forEach((opt) => {
          const months = monthsOf(opt.textContent);
          if (months === null) return;
          const diff = Math.abs(months - qty);
          if (diff < bestDiff) {
            bestDiff = diff;
            best = opt;
          }
        });
        if (best && bestDiff < Infinity) this.frequencySelect.value = best.value;
        this.syncFrequencyUI();
      }

      /* ---- custom branded frequency dropdown (replaces the native select) ---- */
      buildFrequencyUI() {
        const select = this.frequencySelect;
        if (!select || this.freqList) return;

        const wrap = document.createElement('div');
        wrap.className = 'h3o-freq';

        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'h3o-freq__trigger';
        trigger.setAttribute('aria-haspopup', 'listbox');
        trigger.setAttribute('aria-expanded', 'false');
        const label = document.createElement('span');
        label.className = 'h3o-freq__label';
        const caret = document.createElement('span');
        caret.className = 'h3o-freq__caret';
        caret.setAttribute('aria-hidden', 'true');
        trigger.append(label, caret);

        const list = document.createElement('ul');
        list.className = 'h3o-freq__list';
        list.id = select.id + '-listbox';
        list.setAttribute('role', 'listbox');
        list.setAttribute('aria-label', 'Delivery frequency');
        trigger.setAttribute('aria-controls', list.id);
        list.hidden = true;
        Array.from(select.options).forEach((opt) => {
          const li = document.createElement('li');
          li.className = 'h3o-freq__opt';
          li.setAttribute('role', 'option');
          li.tabIndex = -1;
          li.textContent = opt.textContent;
          li.dataset.value = opt.value;
          li.addEventListener('click', () => {
            select.value = opt.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            this.closeFrequency(true);
          });
          list.appendChild(li);
        });

        wrap.append(trigger, list);
        select.style.display = 'none';
        select.setAttribute('tabindex', '-1');
        select.setAttribute('aria-hidden', 'true');
        select.parentNode.insertBefore(wrap, select);

        this.freqTrigger = trigger;
        this.freqLabel = label;
        this.freqList = list;

        trigger.addEventListener('click', (e) => { e.stopPropagation(); this.toggleFrequency(); });
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (list.hidden) this.toggleFrequency();
          } else if (e.key === 'Escape') this.closeFrequency(true);
        });
        list.addEventListener('keydown', (e) => {
          const options = Array.from(list.children);
          const index = options.indexOf(document.activeElement);
          let next = index;
          if (e.key === 'ArrowDown') next = (index + 1) % options.length;
          else if (e.key === 'ArrowUp') next = (index - 1 + options.length) % options.length;
          else if (e.key === 'Home') next = 0;
          else if (e.key === 'End') next = options.length - 1;
          else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            options[index]?.click();
            return;
          } else if (e.key === 'Escape') {
            e.preventDefault();
            this.closeFrequency(true);
            return;
          } else if (e.key === 'Tab') {
            this.closeFrequency(true);
            return;
          } else return;
          e.preventDefault();
          options[next]?.focus();
        });
        document.addEventListener('click', (e) => { if (!wrap.contains(e.target)) this.closeFrequency(); });
        this.syncFrequencyUI();
      }

      syncFrequencyUI() {
        if (!this.freqLabel || !this.frequencySelect) return;
        const opt = this.frequencySelect.selectedOptions[0];
        this.freqLabel.textContent = opt ? opt.textContent : '';
        this.freqTrigger.setAttribute('aria-label', 'Delivery frequency: ' + this.freqLabel.textContent);
        Array.from(this.freqList.children).forEach((li) => {
          li.setAttribute('aria-selected', li.dataset.value === this.frequencySelect.value ? 'true' : 'false');
        });
      }

      toggleFrequency() {
        if (!this.freqList) return;
        const willOpen = this.freqList.hidden;
        this.freqList.hidden = !willOpen;
        this.freqTrigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        if (willOpen) this.freqList.querySelector('[aria-selected="true"]')?.focus();
      }

      closeFrequency(restoreFocus = false) {
        if (!this.freqList) return;
        this.freqList.hidden = true;
        this.freqTrigger.setAttribute('aria-expanded', 'false');
        if (restoreFocus) this.freqTrigger.focus();
      }

      get selectedTier() {
        return this.tiers.find((t) => t.querySelector('.h3o-tier__input').checked) || this.tiers[0];
      }

      get activeUnitCents() {
        if (this.mode === 'subscribe' && this.frequencySelect) {
          const opt = this.frequencySelect.selectedOptions[0];
          const cents = opt ? parseInt(opt.dataset.planCents, 10) : NaN;
          if (!isNaN(cents) && cents > 0) return cents;
        }
        return this.unitCents;
      }

      money(cents) {
        return this.fmt.format(cents / 100);
      }

      selectTier(tier) {
        this.tiers.forEach((t) => t.classList.toggle('is-selected', t === tier));
        if (this.mode === 'subscribe') {
          this.matchFrequencyToTier(parseInt(tier.dataset.qty, 10));
        }
        this.render();
      }

      selectMode(mode) {
        this.mode = mode;
        this.modes.forEach((m) => m.classList.toggle('is-selected', m.dataset.mode === mode));
        if (this.frequencyWrap) this.frequencyWrap.hidden = mode !== 'subscribe';
        if (mode === 'subscribe') {
          this.matchFrequencyToTier(parseInt(this.selectedTier.dataset.qty, 10));
        }
        this.render();
      }

      /* one serving a day → per-day cost = total / (qty × servings).
         Always round UP so we never display a price lower than reality. */
      perDayCents(totalCents, qty) {
        return Math.ceil(totalCents / (qty * this.servings));
      }

      render() {
        const unit = this.activeUnitCents;

        this.tiers.forEach((tier) => {
          const qty = parseInt(tier.dataset.qty, 10);
          const pay = parseInt(tier.dataset.pay, 10);
          /* Anchor is the same tubs WITHOUT the bundle discount, priced in the
             mode the shopper is actually in. That keeps anchor − price ===
             the savings chip in BOTH modes; mixing bases is what produced
             the old "SAVE $99.54" that didn't survive subtraction. */
          const anchor = qty * unit;
          const total = pay * unit;
          const perBottle = Math.round(total / qty);

          const anchorEl = tier.querySelector('[data-tier-anchor]');
          const priceEl = tier.querySelector('[data-tier-price]');
          const perEl = tier.querySelector('[data-tier-per]');
          const saveEl = tier.querySelector('[data-tier-save]');

          /* anchor + per always render (space reserved via CSS min-height) so the
             price row aligns across all three cards; text is just blanked when N/A */
          if (anchorEl) anchorEl.textContent = total < anchor ? this.money(anchor) : '';
          if (priceEl) priceEl.textContent = this.money(total);
          if (perEl) {
            const perDay = this.money(this.perDayCents(total, qty)) + ' / day';
            perEl.textContent = qty > 1 ? this.money(perBottle) + ' / tub · ' + perDay : perDay;
          }
          /* Savings chip = the free tubs this tier gives you, at the one-time
             price. Deliberately NOT stacked with the subscription discount:
             stacking produced a claim ($99.54) that did not survive
             subtraction against the price actually on screen. The
             subscription saving is stated separately on the checkbox row. */
          if (saveEl) {
            const freeTubs = (qty - pay) * unit;
            saveEl.hidden = freeTubs <= 0;
            if (freeTubs > 0) saveEl.textContent = 'SAVE ' + this.money(freeTubs);
          }
        });

        const tier = this.selectedTier;
        const qty = parseInt(tier.dataset.qty, 10);
        const pay = parseInt(tier.dataset.pay, 10);
        const total = pay * unit;
        const anchor = qty * unit;

        if (this.quantityInput) this.quantityInput.value = qty;

        if (this.sellingPlanInput) {
          if (this.mode === 'subscribe' && this.frequencySelect) {
            this.sellingPlanInput.value = this.frequencySelect.value;
            this.sellingPlanInput.disabled = false;
          } else {
            this.sellingPlanInput.value = '';
            this.sellingPlanInput.disabled = true;
          }
        }

        /* ---- subscribe checkbox row ------------------------------------
           States the saving in dollars AND the one-time price it replaces,
           so the price on the tier card is never silently conditional. */
        const subPlanCents = (() => {
          if (!this.frequencySelect) return this.unitCents;
          const opt = this.frequencySelect.selectedOptions[0];
          const cents = opt ? parseInt(opt.dataset.planCents, 10) : NaN;
          return !isNaN(cents) && cents > 0 ? cents : this.unitCents;
        })();

        const oneTimeTotal = pay * this.unitCents;
        const subTotal = pay * subPlanCents;
        const subSaving = oneTimeTotal - subTotal;

        const savePctEl = this.querySelector('[data-sub-save-pct]');
        if (savePctEl) {
          /* integer cents math, floored — never overstates the real discount */
          const pct = Math.floor(((this.unitCents - subPlanCents) * 100) / this.unitCents);
          savePctEl.textContent = pct > 0 ? 'SAVE ' + pct + '%' : '';
          savePctEl.hidden = pct <= 0;
        }

        const savingEl = this.querySelector('[data-sub-saving]');
        if (savingEl) savingEl.textContent = subSaving > 0 ? '\u2212' + this.money(subSaving) : '';

        /* the note spells out exactly what the alternative costs */
        const noteEl = this.querySelector('[data-sub-note]');
        if (noteEl) {
          noteEl.textContent = this.mode === 'subscribe'
            ? 'Cancel anytime. Untick for a one-time order at ' + this.money(oneTimeTotal) + '.'
            : 'Tick to save ' + this.money(subSaving) + ' on this order and every one after.';
        }

        const renewalEl = this.querySelector('[data-sub-renewal]');
        if (renewalEl && this.frequencySelect) {
          const cadence = this.frequencySelect.selectedOptions[0]?.textContent.toLowerCase() || '';
          renewalEl.textContent = 'Renews at ' + this.money(subTotal) + ' ' + cadence +
            ' for ' + qty + (qty === 1 ? ' tub' : ' tubs') + '. Shipping and tax extra.';
        }

        if (this.atcLabel && !this.atcLabel.closest('[disabled]')) {
          const noun = qty === 1 ? 'Tub' : 'Tubs';
          if (this.mode === 'subscribe') {
            this.atcLabel.textContent = 'Subscribe — ' + qty + ' ' + noun + ' · ' + this.money(total);
          } else {
            const prefix = this.atcLabel.dataset.atcPrefix || 'Add';
            this.atcLabel.textContent = prefix + ' ' + qty + ' ' + noun + ' — ' + this.money(total);
          }
        }

        this.updateSticky(tier, total, anchor);
      }

      /* ---- sticky bar ---------------------------------------------------- */
      initSticky() {
        if (!this.stickyBar) return;

        const submitBtn = this.stickyBar.querySelector('[data-sticky-submit]');
        const mainSubmit = this.querySelector('.product-form__submit');
        if (submitBtn && mainSubmit) {
          submitBtn.addEventListener('click', () => {
            mainSubmit.click();
            this.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }

        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
                this.stickyBar.classList.toggle('is-visible', show);
                this.stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
                this.stickyBar.inert = !show;
              });
            },
            { threshold: 0 }
          );
          io.observe(this);
        }
      }

      updateSticky(tier, total, anchor) {
        if (!this.stickyBar) return;
        const deal = this.stickyBar.querySelector('[data-sticky-deal]');
        const price = this.stickyBar.querySelector('[data-sticky-price]');
        const anchorEl = this.stickyBar.querySelector('[data-sticky-anchor]');
        const label = tier.querySelector('.h3o-tier__label');
        const submit = this.stickyBar.querySelector('[data-sticky-submit]');
        if (submit) submit.textContent = this.mode === 'subscribe'
          ? 'Subscribe' : (this.atcLabel?.dataset.atcPrefix || 'Add');

        if (deal && label) {
          deal.textContent = label.textContent + (this.mode === 'subscribe' ? ' · Subscribed' : '');
        }
        if (price) price.textContent = this.money(total);
        if (anchorEl) {
          anchorEl.textContent = total < anchor ? this.money(anchor) : '';
        }
      }
    }
  );
}

/* ==========================================================================
   H3O gallery — stage + thumbnails with crossfade.
   ========================================================================== */

if (!customElements.get('h3o-gallery')) {
  customElements.define(
    'h3o-gallery',
    class H3OGallery extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.h3o-pdp__stage-item'));
        this.thumbs = Array.from(this.querySelectorAll('.h3o-pdp__thumb'));

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => {
            this.show(parseInt(thumb.dataset.thumbIndex, 10));
          });
        });

        /* swipe support on the stage */
        const stage = this.querySelector('.h3o-pdp__stage');
        if (stage) {
          let startX = null;
          stage.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
          stage.addEventListener('touchend', (e) => {
            if (startX === null) return;
            const dx = e.changedTouches[0].clientX - startX;
            if (Math.abs(dx) > 40) this.step(dx < 0 ? 1 : -1);
            startX = null;
          }, { passive: true });
        }
      }

      get activeIndex() {
        return Math.max(0, this.items.findIndex((i) => i.classList.contains('is-active')));
      }

      step(dir) {
        const next = (this.activeIndex + dir + this.items.length) % this.items.length;
        this.show(next);
      }

      show(index) {
        this.items.forEach((item, i) => item.classList.toggle('is-active', i === index));
        this.thumbs.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === index);
          thumb.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
        const active = this.thumbs[index];
        if (active && active.scrollIntoView) {
          active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }
    }
  );
}
